<template lang="pug">
.tabGroup.d-none.d-xl-flex.align-items-center.position-relative.ms-auto.px-4
  ul.list-unstyled.d-flex.m-0.gap-3.position-relative.p-0(role="tablist" ref="itemsContainer")
    span.tabGroup__indicator.position-absolute.top-0.start-0.bg-secondary(:style="indicatorStyle")
    li.tabGroup__item.position-relative.py-2.px-3.fw-bold(
      v-for="(tab, i) in tabs"
      :key="tab"
      :ref="el => tabEls[i] = el"
      role="tab"
      tabindex="0"
      @click="select(tab)"
      @keydown.enter.prevent="select(tab)"
      @keydown.space.prevent="select(tab)"
      @keydown.left.prevent="focusTab(i - 1)"
      @keydown.right.prevent="focusTab(i + 1)"
      :aria-selected="selected === tab"
      :aria-controls="`panel-${tab}`"
    ) {{ tab }}
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  nextTick,
  watch,
  onMounted,
  onBeforeUnmount,
} from 'vue';

const props = defineProps<{
  tabs: string[];
  selected: string;
}>();

const emit = defineEmits<{
  (e: 'update:selected', tab: string): void;
}>();

const tabEls = ref<HTMLElement[]>([]);
const indicatorLeft = ref(0);
const indicatorWidth = ref(0);

function updateIndicator() {
  nextTick(() => {
    const index = props.tabs.indexOf(props.selected);
    const el = tabEls.value[index];
    if (el) {
      indicatorLeft.value = el.offsetLeft;
      indicatorWidth.value = el.offsetWidth;
    }
  });
}

function select(tab: string) {
  emit('update:selected', tab);
}

function focusTab(index: number) {
  const normalizedIndex = (index + props.tabs.length) % props.tabs.length;
  const el = tabEls.value[normalizedIndex];
  el?.focus();
}

watch(() => props.selected, updateIndicator);

onMounted(() => {
  updateIndicator();
  window.addEventListener('resize', updateIndicator);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateIndicator);
});

const indicatorStyle = computed(() => ({
  width: `${indicatorWidth.value}px`,
  transform: `translateX(${indicatorLeft.value}px)`,
}));
</script>
<style scoped lang="scss">
$block: '.tabGroup';

#{$block} {
  $block: &;

  #{$block}__indicator {
    left: 0;
    height: 100%;
    border-radius: 55px;
    transition:
      transform 0.3s ease,
      width 0.3s ease;
    z-index: 0;
    pointer-events: none;
  }

  #{$block}__item {
    cursor: pointer;
    z-index: 1;
    color: #3e2073;
    transition: color 0.3s ease;

    &:hover,
    &:focus {
      color: #cc3366;
    }

    &[aria-selected='true'] {
      color: #ffffff;
    }
  }
}
</style>
